// src/analytics/useCoreTracking.js
import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { track } from "./tracker";
import { EV } from "./events";

export default function useCoreTracking({ consent, user }) {
  const location = useLocation();

  // ✅ normalizuj consent (kod tebe nekad bude boolean true)
  const consentAnalytics =
  consent === true ||
  consent?.analytics === true ||
  consent?.consent_analytics === true ||
  consent?.consentAnalytics === true;

const consentMarketing =
  consent === true ||
  consent?.marketing === true ||
  consent?.consent_marketing === true ||
  consent?.consentMarketing === true;

  const hbRef = useRef(null);
  const isActiveRef = useRef(true);

  const landingSent = useRef(false);
  const lastPathRef = useRef("");

  // 1) session_start (1x)
  useEffect(() => {
    track(EV.SESSION_START, {
      consent_analytics: consentAnalytics,
      consent_marketing: consentMarketing,
      firebase_uid: user?.uid || null,
    });
    
  }, []); // samo jednom

  // 2) page_view + landing_view (na svaku promenu rute)
  useEffect(() => {
    const path = location.pathname + location.search;

    // prevent dupliranja u nekim edge slučajevima
    if (lastPathRef.current === path) return;
    lastPathRef.current = path;

    if (!landingSent.current) {
      landingSent.current = true;
      track(EV.LANDING_VIEW, {
        consent_analytics: consentAnalytics,
        consent_marketing: consentMarketing,
        firebase_uid: user?.uid || null,
        metadata: { first_path: location.pathname, first_search: location.search || "" },
        context: { route: location.pathname },
      });
    }

    track(EV.PAGE_VIEW, {
      consent_analytics: consentAnalytics,
      consent_marketing: consentMarketing,
      firebase_uid: user?.uid || null,
      context: { route: location.pathname },
    });
  }, [
    location.pathname,
    location.search,
    consentAnalytics,
    consentMarketing,
    user?.uid,
  ]);

  // 3) scroll_depth (25/50/75/100) — resetuje se po ruti
  useEffect(() => {
    if (!consentAnalytics) return;

    const sent = new Set();

    const onScroll = () => {
      const h = document.documentElement;
      const scrollTop = h.scrollTop || document.body.scrollTop || 0;
      const scrollHeight =
        (h.scrollHeight || document.body.scrollHeight || 0) - h.clientHeight;

      if (scrollHeight <= 0) return;

      const pct = Math.round((scrollTop / scrollHeight) * 100);

      [25, 50, 75, 100].forEach((p) => {
        if (pct >= p && !sent.has(p)) {
          sent.add(p);
          track(EV.SCROLL_DEPTH, {
            consent_analytics: consentAnalytics,
            consent_marketing: consentMarketing,
            firebase_uid: user?.uid || null,
            metadata: { percent: p },
            context: { route: location.pathname },
          });
        }
      });
    };

    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [location.pathname, consentAnalytics, consentMarketing, user?.uid]);

  // 4) heartbeat (engagement) — 15s, samo kad je tab visible
  useEffect(() => {
    if (!consentAnalytics) return;

    const onVisibility = () => {
      isActiveRef.current = document.visibilityState === "visible";
    };
    document.addEventListener("visibilitychange", onVisibility);

    if (hbRef.current) clearInterval(hbRef.current);

    hbRef.current = setInterval(() => {
      if (!isActiveRef.current) return;

      track(EV.HEARTBEAT, {
        consent_analytics: consentAnalytics,
        consent_marketing: consentMarketing,
        firebase_uid: user?.uid || null,
        metadata: { active_seconds: 15 },
        context: { route: location.pathname },
      });
    }, 15000);

    return () => {
      document.removeEventListener("visibilitychange", onVisibility);
      if (hbRef.current) clearInterval(hbRef.current);
    };
  }, [location.pathname, consentAnalytics, consentMarketing, user?.uid]);

  // 5) outbound_click (global capture)
  useEffect(() => {
    if (!consentAnalytics) return;

    const handler = (e) => {
      const a = e.target?.closest?.("a");
      if (!a) return;

      const href = a.getAttribute("href") || "";

      const isOutbound =
        href.startsWith("http") ||
        href.startsWith("mailto:") ||
        href.startsWith("tel:") ||
        href.includes("wa.me") ||
        href.toLowerCase().includes("whatsapp");

      if (!isOutbound) return;

      track(EV.OUTBOUND_CLICK, {
        consent_analytics: consentAnalytics,
        consent_marketing: consentMarketing,
        firebase_uid: user?.uid || null,
        metadata: { href, text: (a.textContent || "").trim().slice(0, 80) },
        context: { route: location.pathname },
      });
    };

    document.addEventListener("click", handler, true);
    return () => document.removeEventListener("click", handler, true);
  }, [location.pathname, consentAnalytics, consentMarketing, user?.uid]);
}